import {
  Button,
  InlineAlert,
  Page,
  PageActions,
  PageContent,
  PageDescription,
  PageHeader,
  PageTitle,
  PageTitleBlock,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@planuze/ui';
import { RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { SegmentedControl } from '../components/filters/segmented-control.js';
import {
  defaultMetricsFilters,
  type MetricsFiltersValue,
} from '../components/metrics/metrics-filters.js';
import { ModerationTab } from '../components/metrics/tabs/moderation-tab.js';
import { RevenueTab } from '../components/metrics/tabs/revenue-tab.js';
import { SignupsTab } from '../components/metrics/tabs/signups-tab.js';
import { SubscribersTab } from '../components/metrics/tabs/subscribers-tab.js';
import { useMetricsDashboardContent } from '../content/sections/metrics-dashboard.js';
import {
  useMetricsOverview,
  useModerationMetrics,
  useRevenueByPlan,
  useSignupsTimeseries,
} from '../hooks/use-metrics.js';
import type { Currency } from '../lib/format.js';

/* Só os values das abas moram em escopo de módulo — o label é resolvido no render via
   `useMetricsDashboardContent` (a copy bilíngue não pode ser lida fora de render, ADR-0811). */
const TAB_VALUES = ['revenue', 'subscribers', 'signups', 'moderation'] as const;

type MetricsTab = (typeof TAB_VALUES)[number];

function isMetricsTab(value: string): value is MetricsTab {
  return (TAB_VALUES as readonly string[]).includes(value);
}

const CURRENCY_OPTIONS: ReadonlyArray<{ value: Currency; label: string }> = [
  { value: 'BRL', label: 'BRL' },
  { value: 'USD', label: 'USD' },
];

/* Dashboard de métricas em abas com lazy-load (ADR-0353/0359/0704): só a query da aba ativa
   dispara (enabled), evitando renderizar 3 charts + cards + moderação de uma vez. O overview
   fica eager (uma agregação leve) e tem os cards distribuídos por aba, sem duplicação. */
export function MetricsDashboardPage() {
  const copy = useMetricsDashboardContent();
  const [tab, setTab] = useState<MetricsTab>('revenue');
  const [currency, setCurrency] = useState<Currency>('BRL');
  const [filters, setFilters] = useState<MetricsFiltersValue>(defaultMetricsFilters);

  const tabItems: ReadonlyArray<{ readonly value: MetricsTab; readonly label: string }> = [
    { value: 'revenue', label: copy.tabs.revenue },
    { value: 'subscribers', label: copy.tabs.subscribers },
    { value: 'signups', label: copy.tabs.signups },
    { value: 'moderation', label: copy.tabs.moderation },
  ];

  const overview = useMetricsOverview();
  /* Revenue serve Receita e Assinantes (mesma queryKey — uma chamada dedup). */
  const revenue = useRevenueByPlan({ enabled: tab === 'revenue' || tab === 'subscribers' });
  const timeseries = useSignupsTimeseries(
    { from: filters.from, to: filters.to, grain: filters.grain },
    { enabled: tab === 'signups' },
  );
  const moderation = useModerationMetrics({ enabled: tab === 'moderation' });

  const refreshAll = (): void => {
    overview.refetch().catch(() => undefined);
    revenue.refetch().catch(() => undefined);
    timeseries.refetch().catch(() => undefined);
    moderation.refetch().catch(() => undefined);
  };

  return (
    <Page>
      <PageHeader>
        <PageTitleBlock>
          <PageTitle>{copy.pageTitle}</PageTitle>
          <PageDescription>{copy.pageDescription}</PageDescription>
        </PageTitleBlock>
        <PageActions>
          <SegmentedControl<Currency>
            value={currency}
            onChange={setCurrency}
            options={CURRENCY_OPTIONS}
            ariaLabel={copy.currencyAriaLabel}
          />
          <Button variant="outline" size="sm" onClick={refreshAll} aria-label={copy.refresh}>
            <RefreshCw className="size-4" aria-hidden="true" />
            {copy.refresh}
          </Button>
        </PageActions>
      </PageHeader>
      <PageContent className="space-y-6">
        {overview.isError && <InlineAlert variant="destructive">{copy.loadError}</InlineAlert>}
        <Tabs
          value={tab}
          onValueChange={(value) => {
            if (isMetricsTab(value)) setTab(value);
          }}
        >
          <TabsList>
            {tabItems.map((item) => (
              <TabsTrigger key={item.value} value={item.value}>
                {item.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="revenue">
            <RevenueTab
              overview={overview.data}
              revenue={revenue.data}
              loading={revenue.isLoading}
              currency={currency}
            />
          </TabsContent>
          <TabsContent value="subscribers">
            <SubscribersTab
              overview={overview.data}
              revenue={revenue.data}
              loading={revenue.isLoading}
              currency={currency}
            />
          </TabsContent>
          <TabsContent value="signups">
            <SignupsTab
              overview={overview.data}
              filters={filters}
              onFiltersChange={setFilters}
              data={timeseries.data}
              loading={timeseries.isLoading}
            />
          </TabsContent>
          <TabsContent value="moderation">
            <ModerationTab metrics={moderation.data} />
          </TabsContent>
        </Tabs>
      </PageContent>
    </Page>
  );
}
